// search/more/SearchInput.tsx
"use client";
import { X, Loader2, Camera } from "lucide-react";
import { Input } from "@/app/components/shadcn/input";
import { Button } from "@/app/components/shadcn/button";
import { cn } from "@/app/utils/cn";
import useSearch from "@/app/lib/features/search/search.hook";
import type { MicPermission, SpeechStatus } from "@/app/hooks/useSpeechInput";
export interface SearchInputProps {
  value: string;
  onChange: (value: string) => void;
  onFocus?: () => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  placeholder: string;
  isListening: boolean;
  isSupported: boolean;
  interimTranscript: string;
  micPermission: MicPermission;
  status: SpeechStatus;
  onMicToggle: () => void;
  onClear: () => void;
  /** Kamera butonuna tıklanınca, "fotoğrafla ara" modalı açılmadan önce çağrılır (sidebar gibi üst katmanları kapatmak için) */
  onCameraClick?: () => void;
  autoFocus?: boolean;
  className?: string;
}

const SearchInput = ({
  value,
  onChange,
  onFocus,
  onKeyDown,
  placeholder,
  isListening,
  // isSupported,
  interimTranscript,
  // micPermission,
  status,
  // onMicToggle,
  onClear,
  onCameraClick,
  autoFocus,
  className,
}: SearchInputProps) => {
  const { openPhotoSearchSheet } = useSearch();
  const displayValue =
    isListening && interimTranscript ? value + interimTranscript : value;

  const isRequesting = status === "requesting";
  const showClear = !!value && !isListening;
  const showInterim = isListening && !!interimTranscript;

  return (
    <>
      <div className="relative">
        <Input
          value={displayValue}
          onChange={(e) => onChange(e.target.value)}
          onFocus={onFocus}
          onKeyDown={onKeyDown}
          placeholder={
            isRequesting
              ? "İzin bekleniyor..."
              : isListening
                ? "Sizi dinliyorum..."
                : placeholder
          }
          autoFocus={autoFocus}
          className={cn(
            "h-11 px-4 transition-all duration-200",
            showClear ? "pr-28" : "pr-14",
            isListening && "border-red-400 ring-1 ring-red-300 bg-red-50/30",
            isRequesting && "border-yellow-400 ring-1 ring-yellow-300",
            className,
          )}
        />

        <div className="absolute top-1/2 -translate-y-1/2 right-2 flex items-center gap-1">
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            onClick={() => { onCameraClick?.(); openPhotoSearchSheet(); }}
            title="Fotoğrafla ara"
            className="rounded-full size-8 text-gray-500 hover:bg-gray-100"
          >
            <Camera size={20} />
          </Button>

          {/* Clear button — input değeri varsa DOM'a eklenir */}
          {showClear && (
            <Button
              type="button"
              variant="secondary"
              onMouseDown={(e) => e.preventDefault()}
              onClick={onClear}
              className="rounded-full size-6"
            >
              <X size={14} />
            </Button>
          )}
        </div>

        {/* Interim transcript hint — CSS opacity+translateY transition */}
        <div
          className="absolute top-full left-0 right-0 mt-1 px-3 py-1.5 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700 flex items-center gap-2 z-10"
          style={{
            opacity: showInterim ? 1 : 0,
            transform: showInterim ? "translateY(0)" : "translateY(-4px)",
            transition: "opacity 150ms ease, transform 150ms ease",
            pointerEvents: showInterim ? "auto" : "none",
          }}
        >
          <Loader2 size={12} className="animate-spin shrink-0" />
          <span className="truncate italic">{interimTranscript}</span>
        </div>
      </div>
    </>
  );
};

export default SearchInput;